import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import Header from 'components/headers/withGreenBack'
import DotsLoading from 'components/loadings/dotsLoading'
import { SectionError } from 'components/states'
import { faPlain, toman } from 'utilities/helper/format'
import { getCashboxGroupPay } from 'services/cashbox'
import { postTransactions } from 'services/easyPay'
import {
    Card,
    Notice,
    PrimaryButton,
    apiMessage,
    inputClass,
} from 'modules/cashbox/ui'
import { useCashbox, useCashboxMembers } from 'modules/cashbox/hooks'

/**
 * پرداخت گروهی — the manager paying other members' shares in one go.
 *
 * `init-group-pay` returns MEMBERSHIP ids, but a payment transaction's
 * `receiver` is a MEMBER id, so the two lists are joined here on membership id
 * (Android does the same mapping inside `GroupPayMembership.getMemberId`).
 * Getting that wrong credits a stranger's share.
 *
 * The transaction array goes to the same `/api/v1/payment/transactions/`
 * endpoint the rest of the PWA uses, and the response's `pay_url` is the
 * gateway redirect.
 */
export default function CashboxGroupPayPage() {
    const { id } = useParams()
    const { cashbox, isLoading: boxLoading } = useCashbox(id)
    const { data: membersData } = useCashboxMembers(id)

    const { data, isLoading, isError, refetch } = useQuery(
        ['cashbox-group-pay', id],
        () => getCashboxGroupPay(id!),
        { enabled: Boolean(id) }
    )

    const [selected, setSelected] = useState<Set<number>>(new Set())
    const [gatewayId, setGatewayId] = useState<number | ''>('')
    const [submitting, setSubmitting] = useState(false)

    /** membership id -> member id */
    const memberIdByMembership = useMemo(() => {
        const map = new Map<number, number>()
        ;(membersData?.memberships ?? []).forEach((membership) =>
            map.set(membership.id, membership.member_id)
        )
        return map
    }, [membersData])

    const rows = data?.memberships ?? []
    const gateways = (data?.gateways ?? []) as Array<any>

    const selectedRows = rows.filter((row) => selected.has(row.id))
    const total = selectedRows.reduce((sum, row) => sum + row.remaining_amount, 0)
    const commission = selectedRows.reduce((sum, row) => sum + row.commission, 0)
    const unresolved = selectedRows.filter(
        (row) => !memberIdByMembership.has(row.id)
    )

    const cycleId = cashbox?.cycle?.id

    const pay = async () => {
        if (!cycleId) {
            toast.error('نوبت جاری پیدا نشد')
            return
        }
        setSubmitting(true)
        try {
            const payload = selectedRows.map((row) => ({
                ctx_type: 'ccl',
                ctx_id: cycleId,
                source: 'gtw',
                destination:
                    cashbox?.trust_state === 'trst' ? 'hbb' : 'bba',
                amount: row.remaining_amount,
                receiver: memberIdByMembership.get(row.id),
                gateway: gatewayId,
            }))
            const result: any = await postTransactions(payload as any)
            posthog.capture('cashbox_group_pay_started', {
                cashbox_id: id,
                members: selectedRows.length,
                total,
            })
            if (result?.pay_url) window.location.replace(result.pay_url)
            else toast.error('لینک پرداخت دریافت نشد')
        } catch (error) {
            toast.error(apiMessage(error))
        } finally {
            setSubmitting(false)
        }
    }

    if (boxLoading || isLoading) return <DotsLoading />
    if (isError)
        return (
            <div className="bg-white min-h-screen">
                <Header title="پرداخت گروهی" />
                <div className="p-4">
                    <SectionError onRetry={() => refetch()} />
                </div>
            </div>
        )

    if (cashbox?.membership?.role !== 'own')
        return (
            <div className="bg-white min-h-screen">
                <Header title="پرداخت گروهی" />
                <div className="p-4">
                    <Notice tone="warn">
                        فقط مدیر صندوق می‌تواند پرداخت گروهی انجام دهد.
                    </Notice>
                </div>
            </div>
        )

    return (
        <div className="bg-[#FAFAFA] min-h-screen pb-8">
            <div className="bg-white">
                <Header title="پرداخت گروهی" />
            </div>

            <div className="px-4 pt-3">
                <Notice>
                    سهم اعضایی را که هنوز پرداخت نکرده‌اند می‌توانید یکجا
                    بپردازید. مبلغ از حساب شما کسر می‌شود و به سهم آن‌ها ثبت
                    می‌شود.
                </Notice>

                {rows.length === 0 ? (
                    <p className="text-sm text-text-secondary text-center py-10">
                        همه‌ی اعضا سهم این نوبت را پرداخت کرده‌اند
                    </p>
                ) : (
                    <>
                        <div className="mt-3">
                            {rows.map((row) => (
                                <label
                                    key={row.id}
                                    className="flex items-center gap-3 bg-white border border-divider rounded-card p-3 mb-2 cursor-pointer"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selected.has(row.id)}
                                        onChange={() =>
                                            setSelected((prev) => {
                                                const next = new Set(prev)
                                                if (next.has(row.id))
                                                    next.delete(row.id)
                                                else next.add(row.id)
                                                return next
                                            })
                                        }
                                        className="w-4 h-4 accent-green-1"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-black truncate">
                                            {row.name?.trim() ||
                                                row.phone_number}
                                        </p>
                                        <p className="text-xs text-text-secondary mt-0.5">
                                            {row.phone_number}
                                        </p>
                                    </div>
                                    <p className="text-sm font-bold text-black shrink-0">
                                        {toman(row.remaining_amount)}
                                    </p>
                                </label>
                            ))}
                        </div>

                        {selectedRows.length > 0 && (
                            <Card className="mt-3">
                                <div className="flex justify-between items-baseline">
                                    <span className="text-sm text-text-secondary">
                                        {faPlain.format(selectedRows.length)} نفر
                                    </span>
                                    <span className="text-sm font-bold text-black">
                                        {toman(total)}
                                    </span>
                                </div>
                                {commission > 0 && (
                                    <div className="flex justify-between items-baseline mt-2">
                                        <span className="text-sm text-text-secondary">
                                            کارمزد
                                        </span>
                                        <span className="text-sm text-black">
                                            {toman(commission)}
                                        </span>
                                    </div>
                                )}
                                <div className="mt-3">
                                    <select
                                        value={gatewayId}
                                        onChange={(event) =>
                                            setGatewayId(
                                                Number(event.target.value)
                                            )
                                        }
                                        className={inputClass}
                                    >
                                        <option value="">
                                            انتخاب درگاه پرداخت…
                                        </option>
                                        {gateways.map((gateway) => (
                                            <option
                                                key={gateway.id}
                                                value={gateway.id}
                                            >
                                                {gateway.name ??
                                                    gateway.unique_code ??
                                                    `درگاه ${gateway.id}`}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {unresolved.length > 0 && (
                                    <p className="text-xs text-[#B3261E] mt-2">
                                        اطلاعات {faPlain.format(
                                            unresolved.length
                                        )}{' '}
                                        عضو کامل نیست؛ فهرست را به‌روزرسانی
                                        کنید.
                                    </p>
                                )}
                            </Card>
                        )}

                        <div className="mt-4">
                            <PrimaryButton
                                disabled={
                                    selectedRows.length === 0 ||
                                    !gatewayId ||
                                    unresolved.length > 0 ||
                                    !cycleId ||
                                    submitting
                                }
                                onClick={pay}
                            >
                                {submitting
                                    ? 'در حال انتقال به درگاه…'
                                    : `پرداخت ${toman(total + commission)}`}
                            </PrimaryButton>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
